// Client changelog as PandaUnion GET /api/client/changelog serves it, and a
// small parser that turns release notes into blocks the page renders without
// v-html. Notes are a dash list ("- ", sub-items "· "); legacy desktop notes
// also use numbered lines, headings, **bold** and links.

export type ChangelogChannel = 'stable' | 'beta'
export type ChangelogPlatformId = 'desktop' | 'ios' | 'android' | 'router'
export type ChangelogVersion = { version: string, build: number | null }
export type ChangelogRelease = ChangelogVersion & {
  channel: ChangelogChannel
  publishedAt: string | null
  notes: string
  notesEn: string
}
export type PlatformChangelog = {
  current: { stable: ChangelogVersion | null, beta: ChangelogVersion | null }
  releases: ChangelogRelease[]
}
export type ClientChangelog = Record<ChangelogPlatformId, PlatformChangelog | { error: string }>
// One page of one client and line (GET /api/client/changelog?platform=&channel=&offset=&limit=).
export type ChangelogFilter = 'all' | ChangelogChannel
export type DatedChangelogVersion = ChangelogVersion & { publishedAt: string | null }
export type ChangelogPage = {
  current: { stable: DatedChangelogVersion | null, beta: DatedChangelogVersion | null }
  counts: Record<ChangelogFilter, number>
  offset: number
  limit: number
  releases: ChangelogRelease[]
}

export type NoteInline =
  | { kind: 'text' | 'strong' | 'code', text: string }
  | { kind: 'link', text: string, href: string }
export type NoteBlock =
  | { kind: 'item', depth: 0 | 1, marker: string | null, inlines: NoteInline[] }
  | { kind: 'paragraph' | 'heading', inlines: NoteInline[] }

export const releaseLabel = (release: ChangelogVersion) =>
  release.build === null ? release.version : `${release.version} (${release.build})`

export function pickReleaseNotes(release: { notes: string, notesEn: string }, locale: string) {
  const preferEnglish = locale === 'en'
  const preferred = (preferEnglish ? release.notesEn : release.notes).trim()
  if (preferred) return { text: preferred, lang: preferEnglish ? 'en' : 'zh' }
  const other = (preferEnglish ? release.notes : release.notesEn).trim()
  if (other) return { text: other, lang: preferEnglish ? 'zh' : 'en' }
  return { text: '', lang: preferEnglish ? 'en' : 'zh' }
}

// **bold**, `code`, [text](http url), <http url>, bare http url.
const INLINE = /\*\*(.+?)\*\*|`([^`]+)`|\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)|<(https?:\/\/[^>\s]+)>|(https?:\/\/[^\s<>()（），。、；！？]+)/g

const bareLinkText = (href: string) => href.replace(/^https?:\/\//, '')

function parseInlines(text: string): NoteInline[] {
  const parts: NoteInline[] = []
  const pushText = (value: string) => {
    if (!value) return
    const last = parts[parts.length - 1]
    if (last?.kind === 'text') last.text += value
    else parts.push({ kind: 'text', text: value })
  }
  let cursor = 0
  for (const match of text.matchAll(INLINE)) {
    const [whole, strong, code, linkText, linkHref, angleHref] = match
    let bare = match[6]
    let tail = ''
    if (bare) {
      // A sentence that ends right after a URL keeps its ASCII punctuation.
      const trimmed = bare.replace(/[.,;:!?'"]+$/, '')
      tail = bare.slice(trimmed.length)
      bare = trimmed
    }
    pushText(text.slice(cursor, match.index))
    if (strong !== undefined) parts.push({ kind: 'strong', text: strong })
    else if (code !== undefined) parts.push({ kind: 'code', text: code })
    else if (linkText !== undefined) parts.push({ kind: 'link', text: linkText, href: linkHref! })
    else if (angleHref !== undefined) parts.push({ kind: 'link', text: bareLinkText(angleHref), href: angleHref })
    else parts.push({ kind: 'link', text: bareLinkText(bare!), href: bare! })
    pushText(tail)
    cursor = match.index + whole.length
  }
  pushText(text.slice(cursor))
  return parts
}

export function parseChangelogNotes(text: string): NoteBlock[] {
  const blocks: NoteBlock[] = []
  for (const raw of text.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.replace(/\s+$/, '')
    if (!line.trim()) continue
    const indent = /^\s*/.exec(line)![0].length
    const body = line.trim()
    let match: RegExpExecArray | null
    if ((match = /^[-*•]\s+(.*)$/.exec(body))) {
      blocks.push({ kind: 'item', depth: indent >= 2 ? 1 : 0, marker: null, inlines: parseInlines(match[1]!) })
    } else if ((match = /^·\s*(.*)$/.exec(body))) {
      blocks.push({ kind: 'item', depth: 1, marker: null, inlines: parseInlines(match[1]!) })
    } else if ((match = /^(\d+)(?:[.)]\s+|、\s*)(.*)$/.exec(body))) {
      blocks.push({ kind: 'item', depth: indent >= 2 ? 1 : 0, marker: match[1]!, inlines: parseInlines(match[2]!) })
    } else if ((match = /^#{1,6}\s+(.*)$/.exec(body))) {
      blocks.push({ kind: 'heading', inlines: parseInlines(match[1]!) })
    } else {
      blocks.push({ kind: 'paragraph', inlines: parseInlines(body) })
    }
  }
  return blocks
}
